import type { ProductHitWithSwatches } from "~/lib/app.types";

export type MarketingTileSpan = "0" | "1" | "2" | "3" | "4";

/**
 * Shape of a resolved `injectable-marketing-content` entry after Builder
 * enriches the outer reference. Two render paths:
 * - `data.isExternalContentSource === true`  → render `data.embeddedContent` as raw HTML
 * - `data.isExternalContentSource === false` → render the nested `data.builderContent`
 *   reference (typically a Symbol) via `<Content model={...} content={...} />`.
 *
 * The nested `builderContent.value` is populated when the loader fetches with
 * `enrichLevel: 2` (one hop for `injectable-marketing-content`, second for the
 * reference inside it). At `enrichLevel: 1` the nested `value` is undefined.
 */
export interface InjectableMarketingContentValue {
  id?: string;
  data?: {
    isExternalContentSource?: boolean;
    embeddedContent?: string;
    builderContent?: {
      id?: string;
      model?: string;
      value?: Record<string, unknown> | null;
    };
  };
  [key: string]: unknown;
}

/**
 * Each injected-content list item: a reference to an
 * `injectable-marketing-content` entry (with its `.value` populated by the
 * loader's `enrichLevel: 2` fetch) plus the per-breakpoint span fields and the
 * absolute insertion position.
 */
export interface MarketingTileItem {
  content?: {
    "@type"?: string;
    id?: string;
    model?: string;
    value?: InjectableMarketingContentValue | null;
  };
  position?: number;
  largeDesktopSpan?: MarketingTileSpan;
  desktopSpan?: MarketingTileSpan;
  tabletSpan?: MarketingTileSpan;
  mobileSpan?: MarketingTileSpan;
  // Optional author-provided aspect ratio (e.g. "992/1614"). When omitted for
  // external-source tiles, the slot extracts it from the embedded SVG viewBox.
  aspectRatio?: string;
}

export interface CategoryMarketingTilesData {
  categorySlug?: string;
  siteId?: string;
  injectedContentItems?: MarketingTileItem[];
}

export type InterleavedEntry =
  | { kind: "product"; product: ProductHitWithSwatches; key: string }
  | { kind: "marketing"; tile: MarketingTileItem; key: string };

/**
 * Interleave marketing tiles into a flat product list. Mirrors the
 * `paginatedGridDisplay` algorithm from hec-composable: position is an
 * array-insertion index, clamped to `[0, products.length]`, deduplicated by
 * position (first wins). Tiles with no resolved reference are dropped — the
 * data-model entry's publish state controls scheduling, but a missing
 * referenced content shouldn't render an empty cell.
 */
export function interleaveMarketingTiles(
  products: ProductHitWithSwatches[],
  tiles: MarketingTileItem[],
): InterleavedEntry[] {
  const productEntries: InterleavedEntry[] = products.map((product, i) => ({
    kind: "product",
    product,
    key: `product-${product.productId ?? i}`,
  }));

  if (!tiles.length) return productEntries;

  const seenPositions = new Set<number>();
  const insertions: { index: number; entry: InterleavedEntry }[] = [];

  tiles.forEach((tile, i) => {
    const rawPosition = tile.position;
    if (
      typeof rawPosition !== "number" ||
      !Number.isFinite(rawPosition) ||
      rawPosition < 0
    )
      return;
    const value = tile.content?.value;
    if (!value || Object.keys(value).length === 0) return;

    const positionInt = Math.floor(rawPosition);
    const clamped = Math.max(0, Math.min(positionInt, products.length));
    if (seenPositions.has(clamped)) return;
    seenPositions.add(clamped);

    insertions.push({
      index: clamped,
      entry: {
        kind: "marketing",
        tile,
        key: `marketing-${tile.content?.id ?? `pos-${clamped}`}-${i}`,
      },
    });
  });

  insertions.sort((a, b) => a.index - b.index);

  const out: InterleavedEntry[] = [];
  let nextInsertion = 0;
  for (let i = 0; i <= productEntries.length; i++) {
    while (
      nextInsertion < insertions.length &&
      insertions[nextInsertion].index === i
    ) {
      out.push(insertions[nextInsertion].entry);
      nextInsertion++;
    }
    if (i < productEntries.length) out.push(productEntries[i]);
  }

  return out;
}
